import { updateSignUpState } from "../../../store/auth.tsx";
import {
  appendCustomTextResponse,
  appendStreamingTextResponse,
} from "../../../store/conversation.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import axios from "../../../utils/axios.tsx";
import { pushAnalyticsEvent } from "../../../utils/googleEvent.ts";
import { SIGNUP_PROVIDER_STORAGE_KEY } from "../../../const.ts";

const NO_PROVIDER = "not_set";

const normalizeProvider = (value: string | null): string => {
  if (!value) {
    return NO_PROVIDER;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === "google") {
    return "google";
  }
  if (normalizedValue === "microsoft") {
    return "microsoft";
  }
  if (normalizedValue === "visma connect" || normalizedValue === "e-conomic") {
    return "visma_connect";
  }

  return NO_PROVIDER;
};

const handleCompanyName = async (
  enteredText: string,
  dispatch: any,
  provider: string = NO_PROVIDER,
) => {
  const trimmedText = enteredText.trim();

  // If this is the first time calling this handler (no text entered yet), prompt for company name
  if (!enteredText || enteredText === "") {
    const message = i18n._(
      msg({
        message:
          "Great! Now I need to collect some additional information to complete your account setup. Please enter your company name:",
        context: "Message prompting the user to enter their company name.",
      }),
    );
    return dispatch(appendStreamingTextResponse(message, true));
  }

  if (!trimmedText) {
    const message = i18n._(
      msg({
        message:
          "Please enter your company name to continue with the signup process.",
        context: "Message prompting the user to enter their company name.",
      }),
    );
    return dispatch(appendStreamingTextResponse(message, true));
  }

  // Store the company name in the signup state
  dispatch(
    updateSignUpState({
      companyName: trimmedText,
    }),
  );

  // Fetch and display terms and conditions
  const termsResponse = await fetchTermsAndConditions();
  if (!termsResponse) {
    return dispatch(
      appendCustomTextResponse(
        i18n._(
          msg({
            message:
              "I had some issues fetching the terms and conditions. Please raise a support issue.",
            context:
              "Message informing the user of an occurred problem while fetching the terms and conditions and urging him to raise this issue.",
          }),
        ),
      ),
    );
  }

  // Proceed to the next step (terms and conditions)
  const normalizedProvider = normalizeProvider(provider);
  pushAnalyticsEvent("terms_and_conditions", {
    step_number: 4,
    step_name: "accept_terms_and_conditions",
    provider:
      normalizedProvider !== NO_PROVIDER
        ? normalizedProvider
        : normalizeProvider(localStorage.getItem(SIGNUP_PROVIDER_STORAGE_KEY)),
    company_name_entered: "yes",
  });

  dispatch(
    appendStreamingTextResponse(
      i18n._(
        msg({
          message: `Thank you! Your company name "${trimmedText}" has been recorded. 
          
Now, please review and agree to our Terms and Conditions by typing 'Agree'.
Please open and read the <a href="${termsResponse.data.content}" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> 📜.`,
          context:
            "Message confirming company name and prompting for terms agreement.",
        }),
      ),
      true,
    ),
  );
};

const fetchTermsAndConditions = async () => {
  try {
    const termsResponse = await axios.get(
      "/v1/anonymous/terms_and_conditions/",
    );
    if (termsResponse.status !== 200) {
      return null;
    }
    return termsResponse;
  } catch (error) {
    return null;
  }
};

export default handleCompanyName;
